import { useState } from 'react';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import Field from '../components/Field.jsx';
import PasswordField from '../components/PasswordField.jsx';
import Alert from '../components/ui/Alert.jsx';
import { PageHeader, Section } from '../components/ui/index.jsx';
import { checkRequired } from '../utils/validate.js';

const MIN_PASSWORD = 12;

function ProfileForm({ user, onSaved }) {
  const [form, setForm] = useState({ first_name: user.first_name || '', last_name: user.last_name || '' });
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); setNotice(null); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const unchanged = form.first_name.trim() === (user.first_name || '') && form.last_name.trim() === (user.last_name || '');

  const save = async (e) => {
    e.preventDefault();
    const found = Object.fromEntries(Object.entries({
      first_name: checkRequired(form.first_name, 'your first name', { max: 100 }),
      last_name: checkRequired(form.last_name, 'your last name', { max: 100 }),
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true);
    try {
      await api('/portal/account/profile', { method: 'PATCH', body: form });
      await onSaved();
      setNotice({ tone: 'success', text: 'Saved.' });
    } catch (err) {
      setErrors(err.data?.fields || {});
      if (!err.data?.fields) setNotice({ tone: 'danger', text: err.message });
    } finally { setBusy(false); }
  };

  return (
    <Section title="Your details" description="Your name is shown to your teammates, and on approval and invite emails you send.">
      <form onSubmit={save} noValidate>
        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
        <div className="row-2">
          <Field label="First name" required maxLength={100} autoComplete="given-name" value={form.first_name}
            onChange={(e) => set('first_name', e.target.value)} error={errors.first_name} />
          <Field label="Last name" required maxLength={100} autoComplete="family-name" value={form.last_name}
            onChange={(e) => set('last_name', e.target.value)} error={errors.last_name} />
        </div>
        <Field label="Email address" value={user.email} readOnly disabled
          hint="This is how you sign in. To use a different address, ask a colleague to invite it, or contact support@summitshield.io." />
        <button type="submit" className="btn btn-primary form-submit" disabled={busy || unchanged}>{busy ? 'Saving…' : 'Save'}</button>
      </form>
    </Section>
  );
}

function PasswordForm() {
  const empty = { current_password: '', password: '', password_confirm: '' };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); setNotice(null); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const matches = form.password_confirm && form.password === form.password_confirm;

  const save = async (e) => {
    e.preventDefault();
    const found = {};
    if (!form.current_password) found.current_password = 'Enter your current password.';
    if (form.password.length < MIN_PASSWORD) found.password = `Use at least ${MIN_PASSWORD} characters.`;
    else if (form.password.length > 200) found.password = 'Use 200 characters or fewer.';
    else if (form.password !== form.password_confirm) found.password_confirm = 'The passwords don’t match.';
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true);
    try {
      const res = await api('/portal/account/password', { method: 'POST', body: form });
      setForm(empty);
      setNotice({
        tone: 'success',
        text: `Password changed. ${res.other_sessions_ended ? `We signed you out on ${res.other_sessions_ended} other device${res.other_sessions_ended === 1 ? '' : 's'}. ` : ''}We’ve emailed you to say so.`,
      });
    } catch (err) {
      setErrors(err.data?.fields || {});
      if (!err.data?.fields) setNotice({ tone: 'danger', text: err.message });
    } finally { setBusy(false); }
  };

  return (
    <Section title="Change password"
      description="You stay signed in here. Anywhere else you’re signed in is signed out, so if someone else knew your old password, they’re out too.">
      <form onSubmit={save} noValidate>
        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
        <PasswordField label="Current password" autoComplete="current-password" required value={form.current_password}
          onChange={(e) => set('current_password', e.target.value)} error={errors.current_password}
          hint={!errors.current_password ? <>Forgotten it? Sign out and use &ldquo;Forgot password&rdquo;.</> : undefined} />
        <div className="row-2">
          <PasswordField label="New password" autoComplete="new-password" required value={form.password}
            onChange={(e) => set('password', e.target.value)} error={errors.password}
            hint={!errors.password ? `At least ${MIN_PASSWORD} characters. A short phrase is easiest to remember.` : undefined} />
          <PasswordField label="Confirm new password" autoComplete="new-password" required value={form.password_confirm}
            onChange={(e) => set('password_confirm', e.target.value)} error={errors.password_confirm}
            hint={matches && !errors.password_confirm ? '✓ Passwords match' : undefined} />
        </div>
        <button type="submit" className="btn btn-primary form-submit" disabled={busy}>{busy ? 'Changing…' : 'Change password'}</button>
      </form>
    </Section>
  );
}

/** Edit profile: your name and your password. */
export default function Account() {
  const { user, refresh } = useAuth();
  return (
    <div className="page">
      <PageHeader title="Your profile" subtitle="Your own sign-in. Company details are on the Company page." />
      <ProfileForm user={user} onSaved={refresh} />
      <PasswordForm />
    </div>
  );
}
